/**
 * Dynamic Lighting Simulator — WebGL relight (Phase 2) with CSS overlay fallback.
 */
(function () {
  const root = document.querySelector('[data-simulator]');
  if (!root) return;

  const stage = root.querySelector('.sim-stage');
  const baseImg = root.querySelector('.sim-base');
  const light = root.querySelector('.sim-light');
  const grid = root.querySelector('.sim-grid');
  const puck = root.querySelector('.sim-puck');
  const ambient = root.querySelector('.sim-ambient');
  const ctaBtn = root.querySelector('#simulator-cta-link');
  const shopUrl = root.dataset.shopUrl || '/collections/all-in-stock';
  const intensityVal = root.querySelector('#light-intensity-val');
  const ambientVal = root.querySelector('#light-ambient-val');
  const beamVal = root.querySelector('#light-beam-val');
  const tempVal = root.querySelector('#light-temp-val');

  if (!stage || !baseImg || !light || !grid || !puck || !ambient) return;

  const state = {
    x: 50,
    y: 38,
    intensity: 40,
    ambient: 70,
    beam: 120,
    modifier: 'softbox',
    temp: 55,
  };

  let relight = null;
  let useWebGL = false;
  let imageBusy = false;
  let pendingSrc = null;

  function tempColor(t) {
    const warm = [255, 197, 143];
    const neut = [255, 241, 222];
    const cool = [196, 216, 255];
    let a;
    let b;
    let f;
    if (t < 50) {
      a = warm;
      b = neut;
      f = t / 50;
    } else {
      a = neut;
      b = cool;
      f = (t - 50) / 50;
    }
    return a.map((c, i) => Math.round(c + (b[i] - c) * f));
  }

  function kelvinLabel(t) {
    return Math.round(2700 + (t / 100) * 4800) + 'K';
  }

  function beamRadiiPx(beamDeg, soft, intensity, minDim) {
    const I = intensity / 100;
    const t = Math.max(0, Math.min(1, (beamDeg - 15) / 165));
    if (soft) {
      const outer = minDim * (0.28 + t * 0.5 - I * 0.05);
      return { outer: Math.max(40, outer) };
    }
    const outer = minDim * (0.16 + t * 0.3 - I * 0.04);
    return { outer: Math.max(24, outer) };
  }

  function stagePoint() {
    const rect = stage.getBoundingClientRect();
    const w = rect.width || 1;
    const h = rect.height || 1;
    return {
      w,
      h,
      minDim: Math.min(w, h),
      cx: (state.x / 100) * w,
      cy: (state.y / 100) * h,
    };
  }

  function setWebGLMode(active, ready) {
    useWebGL = active;
    root.classList.toggle('sim--webgl', active && !!ready);
    if (active) {
      light.style.opacity = '0';
      if (ready) {
        baseImg.style.opacity = '0';
        baseImg.style.pointerEvents = 'none';
      }
    } else {
      baseImg.style.opacity = '';
      baseImg.style.pointerEvents = '';
      light.style.opacity = '';
      ambient.style.opacity = '';
    }
  }

  function initWebGL() {
    if (!window.GravityRelightEngine || !GravityRelightEngine.isSupported()) return false;
    try {
      relight = new GravityRelightEngine(stage, baseImg);
      setWebGLMode(true, false);
      return true;
    } catch {
      relight = null;
      setWebGLMode(false, false);
      return false;
    }
  }

  function renderAmbientOverlay() {
    const I = state.intensity / 100;
    const A = state.ambient / 100;
    const soft = state.modifier === 'softbox';
    const { w, h, minDim, cx, cy } = stagePoint();
    const { outer } = beamRadiiPx(state.beam, soft, state.intensity, minDim);
    const px = (n) => n.toFixed(1) + 'px';
    const edgeDark = (0.06 + I * 0.12) * (1.2 - A * 0.75);
    const vignetteR = Math.hypot(w, h) * 0.88;
    ambient.style.background =
      'radial-gradient(circle ' +
      px(outer * 2.35) +
      ' at ' +
      px(cx) +
      ' ' +
      px(cy) +
      ', transparent 0, rgba(0,0,0,' +
      (edgeDark * 0.22).toFixed(3) +
      ') ' +
      px(outer * 1.15) +
      ', rgba(0,0,0,' +
      edgeDark.toFixed(3) +
      ') ' +
      px(vignetteR) +
      ')';
    const overlayOpacity = (soft ? 0.55 : 0.72) * (1.15 - A * 0.55);
    ambient.style.opacity = overlayOpacity.toFixed(2);
  }

  function renderCss() {
    const [r, g, b] = tempColor(state.temp);
    const I = state.intensity / 100;
    const soft = state.modifier === 'softbox';
    const { w, h, minDim, cx, cy } = stagePoint();
    const { outer } = beamRadiiPx(state.beam, soft, state.intensity, minDim);
    const px = (n) => n.toFixed(1) + 'px';

    renderAmbientOverlay();

    const coreA = (soft ? 0.2 : 0.34) * (0.35 + I * 0.55);
    const midA = (soft ? 0.09 : 0.16) * (0.35 + I * 0.55);
    light.style.background =
      'radial-gradient(circle ' +
      px(outer) +
      ' at ' +
      px(cx) +
      ' ' +
      px(cy) +
      ', rgba(' +
      r +
      ',' +
      g +
      ',' +
      b +
      ',' +
      Math.min(coreA, 0.55).toFixed(3) +
      ') 0, rgba(' +
      r +
      ',' +
      g +
      ',' +
      b +
      ',' +
      Math.min(midA, 0.35).toFixed(3) +
      ') ' +
      px(outer * 0.52) +
      ', rgba(' +
      r +
      ',' +
      g +
      ',' +
      b +
      ',0) ' +
      px(outer) +
      ')';
    light.style.opacity = soft ? '0.68' : '0.82';

    if (soft) {
      grid.style.opacity = '0';
      grid.style.backgroundSize = '';
      grid.style.webkitMaskImage = '';
      grid.style.maskImage = '';
    } else {
      grid.style.backgroundSize = '12px 12px';
      grid.style.opacity = (0.18 + I * 0.22).toFixed(2);
      const mask =
        'radial-gradient(circle ' +
        px(outer) +
        ' at ' +
        px(cx) +
        ' ' +
        px(cy) +
        ', #000 ' +
        px(outer * 0.38) +
        ', transparent ' +
        px(outer * 0.9) +
        ')';
      grid.style.webkitMaskImage = mask;
      grid.style.maskImage = mask;
    }

    const contrast = soft ? 1.04 + I * 0.07 : 1.12 + I * 0.2;
    const A = state.ambient / 100;
    const brightness = 0.32 + A * 0.62 + I * 0.03;
    baseImg.style.filter =
      'brightness(' +
      brightness.toFixed(2) +
      ') contrast(' +
      contrast.toFixed(2) +
      ') saturate(' +
      (0.98 + (state.temp / 100) * 0.06).toFixed(2) +
      ')';
  }

  function renderGridOverlay() {
    if (!useWebGL) return;
    const I = state.intensity / 100;
    const soft = state.modifier === 'softbox';
    const { minDim, cx, cy } = stagePoint();
    const { outer } = beamRadiiPx(state.beam, soft, state.intensity, minDim);
    const px = (n) => n.toFixed(1) + 'px';

    if (soft) {
      grid.style.opacity = '0';
      grid.style.webkitMaskImage = '';
      grid.style.maskImage = '';
      return;
    }

    grid.style.backgroundSize = '12px 12px';
    grid.style.opacity = (0.16 + I * 0.2).toFixed(2);
    const mask =
      'radial-gradient(circle ' +
      px(outer) +
      ' at ' +
      px(cx) +
      ' ' +
      px(cy) +
      ', #000 ' +
      px(outer * 0.38) +
      ', transparent ' +
      px(outer * 0.9) +
      ')';
    grid.style.webkitMaskImage = mask;
    grid.style.maskImage = mask;
  }

  function renderPuck() {
    const [r, g, b] = tempColor(state.temp);
    puck.style.left = state.x + '%';
    puck.style.top = state.y + '%';
    puck.style.setProperty('--puck', 'rgb(' + r + ',' + g + ',' + b + ')');
  }

  function render() {
    renderPuck();
    if (useWebGL && relight) {
      relight.setState(state);
      relight.draw();
      renderAmbientOverlay();
      renderGridOverlay();
      return;
    }
    renderCss();
  }

  function loadSimulatorImage(src, alt) {
    if (!src) return;
    const loadSrc = highResSrc(src);

    if (useWebGL && relight) {
      if (imageBusy) {
        pendingSrc = { src: loadSrc, alt: alt || '' };
        return;
      }
      imageBusy = true;
      root.classList.add('sim--loading');
      relight
        .setImage(loadSrc)
        .then(function () {
          if (alt) baseImg.alt = alt;
          imageBusy = false;
          root.classList.remove('sim--loading');
          setWebGLMode(true, true);
          if (pendingSrc) {
            const next = pendingSrc;
            pendingSrc = null;
            loadSimulatorImage(next.src, next.alt);
            return;
          }
          render();
        })
        .catch(function () {
          imageBusy = false;
          root.classList.remove('sim--loading');
          relight = null;
          setWebGLMode(false, false);
          setBaseImageFallback(loadSrc, alt);
        });
      return;
    }

    setBaseImageFallback(loadSrc, alt);
  }

  function setBaseImageFallback(src, alt) {
    baseImg.style.opacity = '0';
    const img = new Image();
    img.onload = function () {
      baseImg.src = src;
      baseImg.removeAttribute('srcset');
      if (alt) baseImg.alt = alt;
      baseImg.style.opacity = '1';
      render();
    };
    img.src = src;
  }

  const hoverLightMq = window.matchMedia('(hover: hover) and (pointer: fine)');
  let useHoverLight = hoverLightMq.matches;

  function setFromEvent(e) {
    const rect = stage.getBoundingClientRect();
    const cx = (e.clientX ?? e.touches?.[0]?.clientX ?? 0) - rect.left;
    const cy = (e.clientY ?? e.touches?.[0]?.clientY ?? 0) - rect.top;
    if (cx < 0 || cy < 0 || cx > rect.width || cy > rect.height) return;
    state.x = Math.max(4, Math.min(96, (cx / rect.width) * 100));
    state.y = Math.max(4, Math.min(96, (cy / rect.height) * 100));
    render();
  }

  function bindHoverLight() {
    stage.classList.add('sim-stage--hover');
    stage.classList.remove('grabbing');
    stage.addEventListener('pointermove', onHoverMove);
    stage.addEventListener('pointerenter', onHoverMove);
  }

  function unbindHoverLight() {
    stage.classList.remove('sim-stage--hover');
    stage.removeEventListener('pointermove', onHoverMove);
    stage.removeEventListener('pointerenter', onHoverMove);
  }

  function onHoverMove(e) {
    if (e.pointerType === 'touch') return;
    setFromEvent(e);
  }

  let dragging = false;
  let dragStartX = 0;
  let dragStartY = 0;
  const TAP_PX = 10;

  function bindTouchLight() {
    unbindHoverLight();
    stage.addEventListener('pointerdown', onTouchDown);
    stage.addEventListener('pointermove', onTouchMove);
    stage.addEventListener('pointerup', onTouchEnd);
    stage.addEventListener('pointercancel', onTouchEnd);
  }

  function onTouchDown(e) {
    if (e.pointerType === 'mouse') return;
    dragging = true;
    dragStartX = e.clientX;
    dragStartY = e.clientY;
    stage.setPointerCapture(e.pointerId);
    setFromEvent(e);
    stage.classList.add('grabbing');
  }

  function onTouchMove(e) {
    if (!dragging) return;
    setFromEvent(e);
  }

  function onTouchEnd(e) {
    if (!dragging) return;
    dragging = false;
    stage.classList.remove('grabbing');
    const moved = Math.hypot(e.clientX - dragStartX, e.clientY - dragStartY);
    if (moved < TAP_PX) setFromEvent(e);
  }

  const hintEl = stage.querySelector('.sim-hint');

  function updateHint() {
    if (!hintEl || hintEl.dataset.hintCustom === 'true') return;
    const current = hintEl.textContent.trim();
    const autoHint = /^◐ (Drag|Hover|Tap)/;
    if (current && !autoHint.test(current)) return;
    const hoverText = hintEl.dataset.hintHover || '◐ Hover to move the light';
    const touchText = hintEl.dataset.hintTouch || '◐ Tap or drag to move the light';
    hintEl.textContent = useHoverLight ? hoverText : touchText;
  }

  function applyInteractionMode() {
    useHoverLight = hoverLightMq.matches;
    stage.removeEventListener('pointerdown', onTouchDown);
    stage.removeEventListener('pointermove', onTouchMove);
    stage.removeEventListener('pointerup', onTouchEnd);
    stage.removeEventListener('pointercancel', onTouchEnd);
    unbindHoverLight();
    if (useHoverLight) bindHoverLight();
    else bindTouchLight();
    updateHint();
  }

  applyInteractionMode();
  hoverLightMq.addEventListener('change', applyInteractionMode);

  root.querySelectorAll('[data-ctrl]').forEach((inp) => {
    inp.addEventListener('input', () => {
      state[inp.dataset.ctrl] = +inp.value;
      if (inp.dataset.ctrl === 'intensity' && intensityVal) {
        intensityVal.textContent = inp.value + '%';
      }
      if (inp.dataset.ctrl === 'ambient' && ambientVal) {
        ambientVal.textContent = inp.value + '%';
      }
      if (inp.dataset.ctrl === 'temp' && tempVal) {
        tempVal.textContent = kelvinLabel(+inp.value);
      }
      if (inp.dataset.ctrl === 'beam' && beamVal) {
        beamVal.textContent = Math.round(+inp.value) + '°';
      }
      render();
    });
  });

  root.querySelectorAll('[data-mod]').forEach((btn) => {
    btn.addEventListener('click', () => {
      root.querySelectorAll('[data-mod]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
      btn.setAttribute('aria-pressed', 'true');
      state.modifier = btn.dataset.mod;
      render();
    });
  });

  root.querySelectorAll('[data-swatch]').forEach((btn) => {
    btn.addEventListener('click', () => {
      root.querySelectorAll('[data-swatch]').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      const src = btn.dataset.swatch;
      const href = btn.dataset.href;
      const label = root.querySelector('[data-swatch-name]');
      if (label && btn.dataset.name) label.textContent = btn.dataset.name;
      if (ctaBtn) ctaBtn.href = href || shopUrl;
      if (!src) return;
      loadSimulatorImage(src, btn.dataset.name || '');
    });
  });

  function highResSrc(src) {
    if (!src) return src;
    if (/([?&])width=\d+/.test(src)) {
      return src.replace(/([?&])width=\d+/, '$1width=2400');
    }
    return src + (src.includes('?') ? '&' : '?') + 'width=2400';
  }

  document.addEventListener('gravity:product-image', (e) => {
    const { src, alt } = e.detail || {};
    loadSimulatorImage(src, alt);
    const label = root.querySelector('[data-swatch-name]');
    if (label && alt) label.textContent = alt;
  });

  if (ctaBtn && (!ctaBtn.getAttribute('href') || ctaBtn.getAttribute('href') === '#')) {
    ctaBtn.href = shopUrl;
  }

  initWebGL();

  const initialSrc = root.dataset.initialImage || baseImg.currentSrc || baseImg.src;
  if (initialSrc) {
    loadSimulatorImage(initialSrc, root.dataset.initialName || baseImg.alt || '');
  }

  const activeMod = root.querySelector('[data-mod][aria-pressed="true"]');
  if (activeMod) state.modifier = activeMod.dataset.mod;

  root.querySelectorAll('[data-ctrl]').forEach((inp) => {
    if (inp.dataset.ctrl in state) state[inp.dataset.ctrl] = +inp.value;
  });

  if (tempVal) tempVal.textContent = kelvinLabel(state.temp);
  if (intensityVal) intensityVal.textContent = state.intensity + '%';
  if (ambientVal) ambientVal.textContent = state.ambient + '%';
  if (beamVal) beamVal.textContent = Math.round(state.beam) + '°';

  if (typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(() => render());
    ro.observe(stage);
  } else {
    window.addEventListener('resize', render);
  }

  render();
})();
